import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { WIZ_EVENT_TYPE, WIZ_PHYSIC_POSITION, WizMathUtils } from '@wizeoo/akm-core-lib';
import { WizComponentManager, WizHgroup, WizRoot, WizTileList, WizEventManager, WizEvent, WizTranslationManager, WizIcon, WizVgroup, WizPopUp, PopUpManager } from '@wizeoo/akm-component-lib';
import { WidgetConfigurationItemObj, WidgetConfigurationObj} from '@wizeoo/akm-message-lib';
import { FIELD_KIND, WidgetLine } from '@wizeoo/akm-compute-lib';
import { isEqual } from 'lodash-es';

import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { AlkEvent } from '../../../framework/events/alk.event';
import { WidgetBasic } from '../base/widget-basic';
import { IWidget, IWidgetFilter } from '../../../manager/widgets/i-widget';
import { ViewData } from '../../../model/view.data';
import { PivotFilterExtendedRenderer } from '../pivot/renderers/filters/pivot-filter-extended.renderer';
import { TileConfig } from '../../multi-display/config/multi-display.config';

import { TileSettingsPop } from './settings/tile-settings.pop';
import { TileRenderer } from './renderers/tile.renderer';
import { WidgetEngine } from '../../../manager/widgets/widget.engine';
import { FreedataTileFilterPop } from './settings/freedata-tile-filter.pop';
import { PopUtils } from '../../../framework/components/pop/pop-utils';


@Component({
  selector: 'tile-widget',
  templateUrl: './tile.widget.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** 
 * affichage en mode tuile
 * la pagination est gérée par le tile
 */
export class TileWidget extends WidgetBasic implements AfterViewInit, OnDestroy {

  constructor(elementRef: ElementRef, cdRef: ChangeDetectorRef) {
    super(elementRef, cdRef);
    this.widgetKind = WIDGET_KIND.TILE;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.BASELINE;
    this.settingsPopClassFactory = TileSettingsPop;
  }

  @ViewChild('containerFilter', { read: ViewContainerRef, static: true }) containerFilter: ViewContainerRef; // zone d'ajout des champs de filtre
  @ViewChild('tileList', { read: WizTileList, static: false }) tileList: WizTileList;
  @ViewChild('hgDetailZone', { read: WizHgroup, static: true }) hgDetailZone: WizHgroup; // zone de filtre, pour écouteur sur setFilter
  @ViewChild('moreFilterIcon') moreFilterIcon: WizIcon;
  @ViewChild('vgMain', { read: WizVgroup, static: true }) vgMain: WizVgroup; // zone de filtre, pour écouteur sur setFilter
  

  /* ##############################################################################
                  composants spécifiques
  ############################################################################## */

  public filterComponents: ComponentRef<PivotFilterExtendedRenderer>[]; // les champs de filtre

  public renderer: any = TileRenderer;
  public dimensions: { cols: number, rows: number } = { cols: 200, rows: 100};// 1cell = 20px x 20px soit 1200px x 2000px


  /** configuration des tuiles */
  @Input()
  public tileConfig: TileConfig = null;

  /** passage des éléments de contexte au wizTile pour transmission aux renderers */
  public get fullContext(): any {
    return [this, this.context];
  }

  /** n'afficher que les éléments sélectionnés */
  public onlySelected: boolean = false;

  public get pageSize(): number {
    return 100;
  }

  private _indice: number = 0;
  public get indice(): number {
    if(this._indice > this.maxIndice) {
      this._indice = this.maxIndice;
    }
    
    return this._indice;
  }
  public set indice(value: number) {
    this._indice = value;
  }

  private get maxIndice(): number {
    let res: number = this.mainDS?.getWorkedSource()?.length || 0;
    res = Math.floor(res/this.pageSize);
    return res;
  }

  public get showFirst(): boolean {
    return this.indice > 1;
  }

  public get showPrevious(): boolean {
    return this.indice > 0;
  }

  public get showNext(): boolean {
    return this.indice < this.maxIndice;
  }

  public get showLast(): boolean {
    return this.indice < this.maxIndice -1;
  }

  public get showUnselect(): boolean {
    return this.mainDS?.selectedItems?.length > 0;
  }

  public get showSelected(): boolean {
    return this.mainDS?.selectedItems?.length > 0;
  }

  public get selectMode(): string  {
    if(this.onlySelected) {
      return '$.tile.showonly.all.$';
    }
    return '$.tile.showonly.selected.$';
  }

  /** passage des éléments de contexte au wizTile pour transmission aux renderers */
  // public get countLabel(): string {
  //   let mess: string = ''; 
  //   if(this.maxIndice > 0) {
  //     mess = WizTranslationManager.getInstance().translateKey('$.tile.page.$') +' ' + (this.indice + 1) +'/' + (this.maxIndice + 1);
  //   }
  //   return mess;
  // }
  /** passage des éléments de contexte au wizTile pour transmission aux renderers */
  public get countLabel(): string {
    let mess: string = ''; 
    if(this.mainDS) {
      mess = this.mainDS.getWorkedSource().length + ' / ' + this.mainDS.getSource().length;
      mess += ' ' + WizTranslationManager.getInstance().translateKey('$.widget.elements.$');
    }

    return mess;
  }


   /* ##############################################################################
                    alimentation des données FEI / construction du grid
  ############################################################################### */
  
  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    let mustBuildRows: boolean = false; // on doit construire les lignes
    let mustRefresh: boolean = false; // création mise à jour de la DS de la liste
    let mustBuildFilters: boolean = false; // on doit construire les filtres
    let mustSelect: boolean = false; // on doit jouer la sélection de lignes

    // première création
    if(origin === WIDGET_ACTION_ORIGIN.INIT) {
    }
    
    if(origin === WIDGET_ACTION_ORIGIN.READY) { // je me suis dit tout seul que j'étais prêt
      mustBuildFilters = true;
      mustBuildRows = true;
      mustSelect = true;
      mustRefresh = true;
    }

    // TODO sur configuration, capter les delta filter, colonnes, fields...

    // TODO TODO détecter une modification structurelle en cas 
    // contextDamaged
    // origine: changement de config
    // et mode pivot, sinon les lignes sont toujours OK
    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {   
      mustBuildFilters = true;
      mustBuildRows = true;
      mustRefresh = true;
    } 

    // modification du filtre... on filtre, et on corrige éventuellement la sélection
    // en cas de champ utilisé en colonne, on reconstruit le grid
    if(origin === WIDGET_ACTION_ORIGIN.FILTERING) {
      mustRefresh = true;
    }

    // modification du pool de sélection sur un master
    if(origin === WIDGET_ACTION_ORIGIN.OUTER_SELECT) {
      // console.log('outer select', this.widgetKind, this.fei.uniqueName, this.state);
      mustBuildRows = true;
      mustSelect = true;
      this.mainDS.refresh(); // on applique le filtre
      mustRefresh = true;
    }
    // modification du scenario global ...
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_SCENARIO_UPDATE) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.RECOMPUTE) { // TODO TODO Faire moins bourrin
      mustBuildRows = true;
      mustRefresh = true;
    }

    if(mustBuildFilters) {
      this.buildFilters();
      this.buildFilterDrops();
    }
    if(mustBuildRows) {
      this.buildLines(); // délégué au WidgetBasic
    }

    if(mustSelect) {
      this.feedSelectionFromContext(this._context); // alimentation de la sélection et passage à WIDGET_STATE = SELECTION_COMPLETE
    }

    if(mustRefresh) {
      this.mainDS.refresh();
      this.buildTiles();
    }
    setTimeout(() => {
      this.cdRef.markForCheck();
    }, 0);
    
  }

  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); // je cherche un master. S'il y en a un, READY n'est pas suffisant
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }

    if(this.state >= WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - modification des filtres qui impactent les colonnes
   * - changement de champs en ligne / colonnes / data
   * - changement de filtre
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN): void {
    this.drawOrRefresh(origin);
    super.alterContent(origin);
  }

  private buildTiles(): void {
   // console.log('tile buildTiles', this.context, this.mainDS);
    
    let maxX: number = 0;
    let maxY: number = 0;
    
    if(!!this.context?.ff) {
      this.context.ff.forEach((item: WidgetConfigurationItemObj) => {
        const askedX: number = (item.x + item.cols)*23;
        const askedY: number = (item.y + item.rows)*23;
        if(!!askedX && askedX > maxX) {
          maxX = askedX;
        }
        if(!!askedY && askedY > maxY) {
          maxY = askedY;
        }
      });
      this.dimensions.cols = maxX;
      this.dimensions.rows = maxY;
    }

    // reconstruction des zones de filtre pour les grid et pivots (pas pour les incExc)
    // this.buildFilterDrops();

    super.drawContent();
    // on signale aux renderers
    WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.TILE_RENDERER_UPDATED, this.context));
  
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    WizEventManager.addListener(this, this.tileList, WIZ_EVENT_TYPE.SELECTION_CHANGED, this.innerSelectionChange);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    WizEventManager.removeListener(this, this.tileList, WIZ_EVENT_TYPE.SELECTION_CHANGED);
  }

  /* ##############################################################################
                    gestion des filtres (composants drop)
     ############################################################################## */

  /**
   * interception de modification de filtre pour déclencher un refresh
   */
  protected innerUpdateFilter(e: AlkEvent): void {
    super.innerUpdateFilter(e);
    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING, e.data);
  }

  /** suppression des combos de filtres (avant changement de step, ou sur retour de pop) */
  private clearFilterDrops(): void {
    this.containerFilter.clear(); // vider le container des favoris
    this.filterComponents = []; // on rince la référence aux filtres
    this._needMore = false;
  }


 /** construction des dropdowns de filtre selon le contexte */
  protected buildFilterDrops() {
    this.clearFilterDrops(); // on rince tout
    
    let maxSize: number = this.vgMain?.elementRef?.nativeElement?.offsetWidth;
    let currentSize: number = 0;

    // console.log('size', this.hgDetailZone.elementRef.nativeElement.offsetWidth);

    // construire unique le rendu, pas besoin d'alimenter les collections tant que la drop n'est pas ouverte
    if(this.filters) {
      [...this.filters.values()].forEach((filter: IWidgetFilter) => {
        let compoSize: number = WidgetEngine.getFilterSize(filter);
        if(currentSize + compoSize + 2 <= maxSize - WidgetEngine.ICONS_FILTER_SIZE) {
          const meCompo: ComponentRef<any> = WizComponentManager.instance.addChildTo(
            PivotFilterExtendedRenderer,
            this.containerFilter,
            { instanceProperties: { data: filter } }
          );
          this.filterComponents.push(meCompo);
          currentSize+= compoSize + 7;
        } else {
          this._needMore = true;
        }
        

      });
    }
    this.cdRef.markForCheck();
  }

  private _needMore: boolean = false;
  public get needMore(): boolean {
    return this._needMore;
  }

  public get hasFilter(): boolean {
    return this.filters?.size > 0;
  }

  public get hasActiveFilter(): boolean {
    let res: boolean = false;
    if(this.filters) {
      [...this.filters.values()].forEach((filter: IWidgetFilter) => {
       if(filter.active) {
        if(!!this.templatingMode || !filter.wcio.filtHide)
        res = true;
       }  
      });
    }
    return res;
  }

  /* ##############################################################################
                    gestion spécifique
     ############################################################################## */

    /**
   * FilterFunction du grid, sur la base des filtres
   * @override pour pagination
   * @param item WidgetLine
   * @returns true si visible
   */
  public mainFilterFunction(item: WidgetLine): boolean {
    let result: boolean = super.mainFilterFunction(item);
    
    if(!!this.onlySelected) { // on ne veut que les sélectionnés
      if(this.mainDS.selectedItems.indexOf(item) < 0) {
        return false;
      }
    }
    // if(this.fdvDS.getWorkedSource().length > 100) {
    //   return false;
    // }

    return result;
  }

  /** navigation dans la vue */
  public doNav(way: string): void {
    switch(way) {
      case 'cancel':
        this.onlySelected = false;
        this.mainDS.clearSelection();
        this.mainDS.refresh();
        break;
      case 'first':
        this.indice = 0;
        break;
      case 'previous':
        this.indice = this.indice-1;
        break;
      case 'next':
        this.indice = this.indice+1;
        break;
      case 'last':
        this.indice = this.maxIndice;
        break;
      case 'selected':
        this.onlySelected = !this.onlySelected;
        this.mainDS.refresh();
        break;       
    }
    // console.log('doNav');
  }

  /**
   * changement de mon pool de sélection... intercepté sur AgGrid
   * on va informer les éventuels esclaves.
   * @param e 
   */
    public innerSelectionChange(e: WizEvent): void {
      if(this.state < WIDGET_STATE.READY) { // pas encore affiché, inutile
        return;
      }
      this.onSelectionChanged(); // persistence du contexte de sélection
      this.updateComponentSelection(); // informer le monde que le master (éventuel) a changé
    }

  /** mémorisation du contexte de sélection */
  public onSelectionChanged(): void {
    if(!this._context) { // inutile si le contexte n'est pas instancié (incexc, vsp)
      return; 
    }
    
    // on fait des paquets de 50 en fonction de l'indice
    // dans ces paquets de 1000, on donne un ordre binaire 2^indice
    // on stocke jusqu'a 50 chiffres, {indice, valeur}
    // exemple, sélection 1, 10, 1001 et 3000
    // 0#1025|10#1|29#1.26765E+30
    const selectMap: Map<number, number> = new Map<number, number>();
    let rankPart: number;
    let binRankPart: number;

    this.mainDS.selectedItems?.forEach((line: WidgetLine) => {
      rankPart = +line.binKey.split(ViewData.CONTEXT_SEPARATOR)[0];
      binRankPart = +line.binKey.split(ViewData.CONTEXT_SEPARATOR)[1];

      if (!selectMap.has(rankPart)) {
        selectMap.set(rankPart, binRankPart);
      } else {
        selectMap.set(rankPart, selectMap.get(rankPart) + binRankPart);
      }
    });

    const selKey: any[] = []; // alimentation des clés dans le contexte
    selectMap.forEach((val: number, key: number) => {
      selKey.push({ k: key, v: val });
    });

    const context: WidgetConfigurationObj = this._context;
    
    // d'abord on vérifie que ça a changé
    let hasUpdate: boolean = !isEqual(context.sel, selKey);
    context.sel = selKey;

    this.updateContext(context, hasUpdate);
    // TODO TODO passer leDrawOr
    // this.agGrid.api.redrawRows(); // refresh sur le rendu du grid
  }

  /**
   * alimentation initiale de la sélection
   * @param context 
   */
  public feedSelectionFromContext(context: WidgetConfigurationObj): void {
    const selectPool: WidgetLine[] = [];
    
    if (context.sel?.length > 0) { // pas de context alimenté sur incExc

      // TODO contrôle de cohérence des sélections obsolètes ?
      const selectionKeyMap: Map<number, number> = new Map(context.sel.map((obj) => [+obj.k, +obj.v]));

      let rankPart: number;
      let binRankPart: number;
      this.mainDS.getWorkedSource().forEach((line: WidgetLine) => {
        rankPart = +line.binKey.split(ViewData.CONTEXT_SEPARATOR)[0];
        binRankPart = +line.binKey.split(ViewData.CONTEXT_SEPARATOR)[1];
        if (selectionKeyMap.has(rankPart) && WizMathUtils.wizBitwize(selectionKeyMap.get(rankPart), binRankPart) > 0) {
          selectPool.push(line);
        }
      });
      
      // besoin du timeOut, sinon pas d'application...
      setTimeout(() => {
        this.mainDS.select(selectPool);
        this.mainDS.refresh(); // on rafraichit
        // console.log('applySelection', this.widgetKind, this.fei.uniqueName, this.mainDS.selectedItems);
        this.state = WIDGET_STATE.SELECTION_COMPLETE; // et on passe à l'état sélectionné
        // ma sélection est OK, je signale pour les éventuels esclaves
        // console.log('feedSelectionFromContext', this.widgetKind, this.fei.uniqueName);
        WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this)); 
      });
    } else {

      // console.log('feedSelectionFromContext', this.fei.uniqueName, this.state);
      this.state = WIDGET_STATE.SELECTION_COMPLETE; // et on passe à l'état sélectionné
      // pas de sélection, on dispatch tout de suite
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this)); 
    }
  }

  /** demande d'export depuis le bouton */
  public exportDatas(kind?: string): void {
    if(!kind || kind === 'excel') {
      // pas d'export Excel pour lui this.model.excelExportEngine.exportAsExcel('allkeyme', this);
    } else {
      this.model.excelExportEngine.exportAsPdf('allkeyme', this, this.elementRef, null, null);
    }
  }


 
  public clearAllFilter(): void {

    // ne pas rincer les filtres masqués
    if(this.filters) { // on rince les IWidgetFilter
      [...this.filters.values()].forEach((filter: IWidgetFilter) => {
        if(!!this.templatingMode || !filter.wcio.filtHide) {
          filter.values = null;
          filter.regex = null;
          filter.active = false;
        }
        
      });
    }
    
    // on rince le contexte
    this.context.f.forEach(wcio => {
      if(!!this.templatingMode || !wcio.filtHide) {
        wcio.sel = null;
      wcio.filtExp = null;
      }
    });

    // on rince les instances
    this.filterComponents.forEach(pfer => {
      if(!!this.templatingMode || !pfer.instance.context.filtHide) {
        pfer.instance.clearFilter(false); // clear sans dispatch
      }
      
    });

    // persistence uniquement hors du template, sinon, c'est délégué
    if (!this.templatingMode) { 
        this._saveContext();
    } else { // mise à jour du template, pas du contexte
        this.settings = this.context;
        WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_SET_SETTINGS, this));
    }     

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING);// et application du filtre
  }
  
  /**
   * détection du resize pour redessiner les filtres
   * @override
   * @param width 
   * @param height 
   */
  public onResize(width, height): void {
    if(this._previousSize && this._previousSize.width !== width) {
      this.buildFilterDrops();
    }
    super.onResize(width, height);
  }

  /**
   * pop de liste des filtres
   */
  public moreFilterPop(): void {

    const pop: WizPopUp = new WizPopUp(
      FreedataTileFilterPop,
      false,
      false,
      WIZ_PHYSIC_POSITION.MIDDLE_CENTER,
      FreedataTileFilterPop.width,
      FreedataTileFilterPop.height,
    );

    const pos: DOMRect = this.moreFilterIcon.elementRef.nativeElement.getBoundingClientRect();
    const bestXY: [number, number] = PopUtils.setPopCoord(pos, pop, WIZ_PHYSIC_POSITION.BOTTOM_RIGHT);
    const x: string = bestXY[0] + 'px';
    const y: string = bestXY[1] + 'px';
    const targetPosition: any = { left: x, top: y }; // position physique
    pop.customPosition = targetPosition;
   
    pop.setCallback(this.backFromFilterPop.bind(this));

    pop.datas = [this,this.filters];

    if (PopUpManager.exist(pop)) { // suppression de l'instance existante
      pop.hide();
    }
    pop.show();
  }

  private backFromFilterPop(updates: Map<string, IWidgetFilter>): void {
 
    if(updates?.size > 0) { // on reconstruit les filtres
      
      // les contextes de filtre
      const filterMap: Map<string, WidgetConfigurationItemObj> = new Map(this.context.f.map((item) => [item.cod + item.fdvf, item]));

      [...updates.keys()].forEach(k => {
          let up: IWidgetFilter = updates.get(k);
          let existWcio = filterMap.get(k);
          if (up.active) { // seulement si le filtre est actif
            if(up.fkd?.datakind === FIELD_KIND.LIST) {
              existWcio.sel = up.values?.size > 0 ? [...up.values].join(ViewData.DOUBLEKEY_SEPARATOR) : null;
            } else {
              existWcio.sel = up.values?.size > 0 ? [...up.values].join(ViewData.KEY_SEPARATOR) : null;
            }
            existWcio.filtExp = up.regex;
            
          } else {
            existWcio.sel = null; // null car '' signifie qu'il y a quand même un filtre (=> filtre sur les valeurs vides)
            existWcio.filtExp = null;
          }
      });

      this.buildFilters();
      this.buildFilterDrops();
      
      // persistence uniquement hors du template, sinon, c'est délégué
      if (!this.templatingMode) { 
          this._saveContext();
      } else { // mise à jour du template, pas du contexte
          this.settings = this.context;
          WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_SET_SETTINGS, this));
      }  

      // et on filtre
      this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING);// et application du filtre
    }

  }


}
